import type { SupportedLocale } from "../../lib/locales";

export type UiLocale = SupportedLocale;

export type SignupPortalCopy = {
  overline: string;
  title: string;
  subtitle: string;
  methodTelegram: string;
  methodEmail: string;
  methodSms: string;
  country: string;
  language: string;
  emailPlaceholder: string;
  phonePlaceholder: string;
  captchaPlaceholder: string;
  acceptTerms: string;
  starting: string;
  continueTelegram: string;
  sending: string;
  sendMagicLink: string;
  sendOtp: string;
  verificationId: string;
  verificationCodePlaceholder: string;
  verifying: string;
  verifyAndContinue: string;
  qrTitle: string;
  qrBody: string;
  signupQrAlt: string;
  devNote: string;
  noTelegramLink: string;
  failedStartSignup: string;
  failedSendVerification: string;
  verificationFailed: string;
  magicLinkSent: string;
  otpSent: string;
  devVerificationCode: string;
  devOtpCode: string;
};

type SignupPortalCopyKey = keyof SignupPortalCopy;

const COPY_TRANSLATION_KEYS: Record<SignupPortalCopyKey, string> = {
  overline: "signupPortal.overline",
  title: "signupPortal.title",
  subtitle: "signupPortal.subtitle",
  methodTelegram: "signupPortal.method.telegram",
  methodEmail: "signupPortal.method.email",
  methodSms: "signupPortal.method.sms",
  country: "signupPortal.field.country",
  language: "signupPortal.field.language",
  emailPlaceholder: "signupPortal.placeholder.email",
  phonePlaceholder: "signupPortal.placeholder.phone",
  captchaPlaceholder: "signupPortal.placeholder.captcha",
  acceptTerms: "signupPortal.terms.accept",
  starting: "signupPortal.action.starting",
  continueTelegram: "signupPortal.action.continueTelegram",
  sending: "signupPortal.action.sending",
  sendMagicLink: "signupPortal.action.sendMagicLink",
  sendOtp: "signupPortal.action.sendOtp",
  verificationId: "signupPortal.verification.id",
  verificationCodePlaceholder: "signupPortal.verification.codePlaceholder",
  verifying: "signupPortal.action.verifying",
  verifyAndContinue: "signupPortal.action.verifyAndContinue",
  qrTitle: "signupPortal.qr.title",
  qrBody: "signupPortal.qr.body",
  signupQrAlt: "signupPortal.qr.alt",
  devNote: "signupPortal.dev.note",
  noTelegramLink: "signupPortal.error.noTelegramLink",
  failedStartSignup: "signupPortal.error.failedStartSignup",
  failedSendVerification: "signupPortal.error.failedSendVerification",
  verificationFailed: "signupPortal.error.verificationFailed",
  magicLinkSent: "signupPortal.info.magicLinkSent",
  otpSent: "signupPortal.info.otpSent",
  devVerificationCode: "signupPortal.info.devVerificationCode",
  devOtpCode: "signupPortal.info.devOtpCode",
};

export const SIGNUP_PORTAL_UI_LOCALES: UiLocale[] = ["en", "ru", "es", "de", "fa"];

export const SIGNUP_PORTAL_COPY: Record<UiLocale, SignupPortalCopy> = {
  en: {
    overline: "Salon OS",
    title: "Start your salon in minutes",
    subtitle: "Self-service onboarding with Telegram-first flow, plus Email/SMS fallback.",
    methodTelegram: "Telegram",
    methodEmail: "Email",
    methodSms: "SMS",
    country: "Country",
    language: "Language",
    emailPlaceholder: "owner@example.com",
    phonePlaceholder: "+1 (555) 000-0000",
    captchaPlaceholder: "CAPTCHA token (optional unless required)",
    acceptTerms: "I accept the Terms and Privacy Policy.",
    starting: "Starting...",
    continueTelegram: "Continue with Telegram",
    sending: "Sending...",
    sendMagicLink: "Send Magic Link",
    sendOtp: "Send OTP",
    verificationId: "Verification ID",
    verificationCodePlaceholder: "Verification code",
    verifying: "Verifying...",
    verifyAndContinue: "Verify and Continue Onboarding",
    qrTitle: "QR Onboarding Entry",
    qrBody: "Use this QR code in in-person campaigns to open the same `/signup` flow.",
    signupQrAlt: "Signup QR",
    devNote: "In dev/local, Email/SMS verification endpoints return a `dev_code` so you can complete the flow without external providers.",
    noTelegramLink: "No Telegram link returned",
    failedStartSignup: "Failed to start signup",
    failedSendVerification: "Failed to send verification",
    verificationFailed: "Verification failed",
    magicLinkSent: "Magic link sent.",
    otpSent: "OTP sent.",
    devVerificationCode: "Dev verification code: {{code}}",
    devOtpCode: "Dev OTP code: {{code}}",
  },
  ru: {
    overline: "Salon OS",
    title: "Запустите салон за несколько минут",
    subtitle: "Самостоятельная регистрация с Telegram-first сценарием и резервным Email/SMS.",
    methodTelegram: "Telegram",
    methodEmail: "Email",
    methodSms: "SMS",
    country: "Страна",
    language: "Язык",
    emailPlaceholder: "owner@example.com",
    phonePlaceholder: "+1 (555) 000-0000",
    captchaPlaceholder: "Токен CAPTCHA (необязательно, если не требуется)",
    acceptTerms: "Я принимаю Условия и Политику конфиденциальности.",
    starting: "Запуск...",
    continueTelegram: "Продолжить через Telegram",
    sending: "Отправка...",
    sendMagicLink: "Отправить magic link",
    sendOtp: "Отправить OTP",
    verificationId: "ID проверки",
    verificationCodePlaceholder: "Код подтверждения",
    verifying: "Проверка...",
    verifyAndContinue: "Проверить и продолжить onboarding",
    qrTitle: "QR для входа в onboarding",
    qrBody: "Используйте этот QR-код в офлайн-кампаниях, чтобы открыть тот же поток `/signup`.",
    signupQrAlt: "QR регистрации",
    devNote: "В dev/local endpoints проверки Email/SMS возвращают `dev_code`, чтобы можно было завершить поток без внешних провайдеров.",
    noTelegramLink: "Ссылка Telegram не получена",
    failedStartSignup: "Не удалось начать регистрацию",
    failedSendVerification: "Не удалось отправить проверку",
    verificationFailed: "Проверка не удалась",
    magicLinkSent: "Magic link отправлен.",
    otpSent: "OTP отправлен.",
    devVerificationCode: "Dev код проверки: {{code}}",
    devOtpCode: "Dev OTP код: {{code}}",
  },
  es: {
    overline: "Salon OS",
    title: "Inicia tu salón en minutos",
    subtitle: "Onboarding de autoservicio con flujo Telegram-first y respaldo por Email/SMS.",
    methodTelegram: "Telegram",
    methodEmail: "Correo",
    methodSms: "SMS",
    country: "País",
    language: "Idioma",
    emailPlaceholder: "owner@example.com",
    phonePlaceholder: "+1 (555) 000-0000",
    captchaPlaceholder: "Token CAPTCHA (opcional salvo que sea obligatorio)",
    acceptTerms: "Acepto los Términos y la Política de Privacidad.",
    starting: "Iniciando...",
    continueTelegram: "Continuar con Telegram",
    sending: "Enviando...",
    sendMagicLink: "Enviar enlace mágico",
    sendOtp: "Enviar OTP",
    verificationId: "ID de verificación",
    verificationCodePlaceholder: "Código de verificación",
    verifying: "Verificando...",
    verifyAndContinue: "Verificar y continuar onboarding",
    qrTitle: "Entrada QR de onboarding",
    qrBody: "Usa este código QR en campañas presenciales para abrir el mismo flujo `/signup`.",
    signupQrAlt: "QR de registro",
    devNote: "En dev/local, los endpoints de verificación por Email/SMS devuelven `dev_code` para completar el flujo sin proveedores externos.",
    noTelegramLink: "No se devolvió enlace de Telegram",
    failedStartSignup: "No se pudo iniciar el registro",
    failedSendVerification: "No se pudo enviar la verificación",
    verificationFailed: "La verificación falló",
    magicLinkSent: "Enlace mágico enviado.",
    otpSent: "OTP enviado.",
    devVerificationCode: "Código de verificación dev: {{code}}",
    devOtpCode: "Código OTP dev: {{code}}",
  },
  de: {
    overline: "Salon OS",
    title: "Starten Sie Ihren Salon in wenigen Minuten",
    subtitle: "Self-Service-Onboarding mit Telegram-first-Ablauf und E-Mail/SMS-Fallback.",
    methodTelegram: "Telegram",
    methodEmail: "E-Mail",
    methodSms: "SMS",
    country: "Land",
    language: "Sprache",
    emailPlaceholder: "owner@example.com",
    phonePlaceholder: "+1 (555) 000-0000",
    captchaPlaceholder: "CAPTCHA-Token (optional, falls erforderlich)",
    acceptTerms: "Ich akzeptiere die Nutzungsbedingungen und die Datenschutzerklärung.",
    starting: "Wird gestartet...",
    continueTelegram: "Mit Telegram fortfahren",
    sending: "Wird gesendet...",
    sendMagicLink: "Magic Link senden",
    sendOtp: "OTP senden",
    verificationId: "Verifizierungs-ID",
    verificationCodePlaceholder: "Verifizierungscode",
    verifying: "Wird verifiziert...",
    verifyAndContinue: "Verifizieren und Onboarding fortsetzen",
    qrTitle: "QR-Onboarding-Einstieg",
    qrBody: "Verwenden Sie diesen QR-Code in Vor-Ort-Kampagnen, um denselben `/signup`-Ablauf zu öffnen.",
    signupQrAlt: "Anmelde-QR",
    devNote: "In dev/local liefern die Email/SMS-Verifizierungsendpunkte einen `dev_code`, damit Sie den Ablauf ohne externe Anbieter abschließen können.",
    noTelegramLink: "Kein Telegram-Link zurückgegeben",
    failedStartSignup: "Registrierung konnte nicht gestartet werden",
    failedSendVerification: "Verifizierung konnte nicht gesendet werden",
    verificationFailed: "Verifizierung fehlgeschlagen",
    magicLinkSent: "Magic Link gesendet.",
    otpSent: "OTP gesendet.",
    devVerificationCode: "Dev-Verifizierungscode: {{code}}",
    devOtpCode: "Dev-OTP-Code: {{code}}",
  },
  fa: {
    overline: "Salon OS",
    title: "سالن خود را در چند دقیقه راه‌اندازی کنید",
    subtitle: "راه‌اندازی خودکار با جریان Telegram-first و پشتیبان Email/SMS.",
    methodTelegram: "تلگرام",
    methodEmail: "ایمیل",
    methodSms: "پیامک",
    country: "کشور",
    language: "زبان",
    emailPlaceholder: "owner@example.com",
    phonePlaceholder: "+1 (555) 000-0000",
    captchaPlaceholder: "توکن CAPTCHA (اختیاری مگر اینکه لازم باشد)",
    acceptTerms: "شرایط استفاده و سیاست حریم خصوصی را می‌پذیرم.",
    starting: "در حال شروع...",
    continueTelegram: "ادامه با تلگرام",
    sending: "در حال ارسال...",
    sendMagicLink: "ارسال لینک جادویی",
    sendOtp: "ارسال رمز یک‌بارمصرف",
    verificationId: "شناسه تأیید",
    verificationCodePlaceholder: "کد تأیید",
    verifying: "در حال تأیید...",
    verifyAndContinue: "تأیید و ادامه راه‌اندازی",
    qrTitle: "ورود QR برای راه‌اندازی",
    qrBody: "از این کد QR در کمپین‌های حضوری استفاده کنید تا همان مسیر `/signup` باز شود.",
    signupQrAlt: "QR ثبت‌نام",
    devNote: "در محیط dev/local، endpointهای تأیید Email/SMS یک `dev_code` برمی‌گردانند تا بدون ارائه‌دهنده خارجی بتوانید مسیر را کامل کنید.",
    noTelegramLink: "لینک تلگرام برگردانده نشد",
    failedStartSignup: "شروع ثبت‌نام ناموفق بود",
    failedSendVerification: "ارسال تأیید ناموفق بود",
    verificationFailed: "تأیید ناموفق بود",
    magicLinkSent: "لینک جادویی ارسال شد.",
    otpSent: "رمز یک‌بارمصرف ارسال شد.",
    devVerificationCode: "کد تأیید dev: {{code}}",
    devOtpCode: "کد OTP dev: {{code}}",
  },
};

export const SIGNUP_PORTAL_LANGUAGE_LABELS: Record<UiLocale, Record<string, string>> = {
  en: { en: "English", es: "Spanish", ru: "Russian", de: "German", fa: "Persian", pt: "Portuguese", nl: "Dutch", fr: "French" },
  ru: { en: "Английский", es: "Испанский", ru: "Русский", de: "Немецкий", fa: "Персидский", pt: "Португальский", nl: "Нидерландский", fr: "Французский" },
  es: { en: "Inglés", es: "Español", ru: "Ruso", de: "Alemán", fa: "Persa", pt: "Portugués", nl: "Neerlandés", fr: "Francés" },
  de: { en: "Englisch", es: "Spanisch", ru: "Russisch", de: "Deutsch", fa: "Persisch", pt: "Portugiesisch", nl: "Niederländisch", fr: "Französisch" },
  fa: { en: "انگلیسی", es: "اسپانیایی", ru: "روسی", de: "آلمانی", fa: "فارسی", pt: "پرتغالی", nl: "هلندی", fr: "فرانسوی" },
};

export function isSignupPortalUiLocale(value: string): value is UiLocale {
  return SIGNUP_PORTAL_UI_LOCALES.includes(value as UiLocale);
}

export function formatSignupPortalCopy(template: string, vars: Record<string, string>) {
  return Object.entries(vars).reduce((acc, [key, value]) => acc.split(`{{${key}}}`).join(value), template);
}

export const SIGNUP_PORTAL_TRANSLATIONS: Record<UiLocale, Record<string, string>> = SIGNUP_PORTAL_UI_LOCALES.reduce(
  (acc, locale) => {
    const copy = SIGNUP_PORTAL_COPY[locale];
    const labels = SIGNUP_PORTAL_LANGUAGE_LABELS[locale];
    const translations: Record<string, string> = {};

    for (const key of Object.keys(COPY_TRANSLATION_KEYS) as SignupPortalCopyKey[]) {
      translations[COPY_TRANSLATION_KEYS[key]] = copy[key];
    }
    for (const code of Object.keys(labels)) {
      translations[`signupPortal.locale.${code}`] = labels[code];
    }

    acc[locale] = translations;
    return acc;
  },
  {} as Record<UiLocale, Record<string, string>>
);
